import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SignaturePadOptions {
    minWidth?: number;
    maxWidth?: number;
    canvasWidth?: number;
    canvasHeight?: number;
    backgroundColor?: string;
    penColor?: string;
}

@Component({
    selector: 'app-signature-pad',
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.scss'],
    standalone: true,
    imports: [CommonModule]
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy {
    @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input() options: SignaturePadOptions = {
        minWidth: 1,
        maxWidth: 1,
        canvasWidth: 600,
        canvasHeight: 200,
        backgroundColor: 'rgb(255,255,255)',
        penColor: 'rgb(0,0,0)'
    };

    @Output() drawStart = new EventEmitter<MouseEvent | Touch>();
    @Output() drawEnd = new EventEmitter<MouseEvent | Touch>();

    private canvas!: HTMLCanvasElement;
    private ctx!: CanvasRenderingContext2D;
    private isDrawing = false;
    private lastX = 0;
    private lastY = 0;
    private velocity = 0;
    private lastVelocity = 0;
    private lastTime = 0;

    private boundHandleMouseDown = this.handleMouseDown.bind(this);
    private boundHandleMouseMove = this.handleMouseMove.bind(this);
    private boundHandleMouseUp = this.handleMouseUp.bind(this);
    private boundHandleTouchStart = this.handleTouchStart.bind(this);
    private boundHandleTouchMove = this.handleTouchMove.bind(this);
    private boundHandleTouchEnd = this.handleTouchEnd.bind(this);

    ngAfterViewInit(): void {
        this.initCanvas();
    }

    ngOnDestroy(): void {
        this.removeEventListeners();
    }

    @HostListener('window:resize')
    onResize(): void {
        this.resizeCanvas();
    }

    private initCanvas(): void {
        this.canvas = this.canvasRef.nativeElement;
        this.ctx = this.canvas.getContext('2d')!;

        this.resizeCanvas();
        this.setupCanvas();
        this.addEventListeners();
    }

    private setupCanvas(): void {
        this.ctx.fillStyle = this.options.backgroundColor || 'rgb(255,255,255)';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.strokeStyle = this.options.penColor || 'rgb(0,0,0)';
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
    }

    private resizeCanvas(): void {
        const rect = this.canvas.getBoundingClientRect();
        const width = this.options.canvasWidth || rect.width || 600;
        const height = this.options.canvasHeight || rect.height || 200;

        // Set the CSS size first
        this.canvas.style.width = width + 'px';
        this.canvas.style.height = height + 'px';

        // Set the actual canvas dimensions
        this.canvas.width = width;
        this.canvas.height = height;

        this.setupCanvas();
    }

    private addEventListeners(): void {
        // Mouse events
        this.canvas.addEventListener('mousedown', this.boundHandleMouseDown);
        this.canvas.addEventListener('mousemove', this.boundHandleMouseMove);
        this.canvas.addEventListener('mouseup', this.boundHandleMouseUp);
        this.canvas.addEventListener('mouseout', this.boundHandleMouseUp);

        // Touch events
        this.canvas.addEventListener('touchstart', this.boundHandleTouchStart, { passive: false });
        this.canvas.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false });
        this.canvas.addEventListener('touchend', this.boundHandleTouchEnd, { passive: false });
    }

    private removeEventListeners(): void {
        this.canvas.removeEventListener('mousedown', this.boundHandleMouseDown);
        this.canvas.removeEventListener('mousemove', this.boundHandleMouseMove);
        this.canvas.removeEventListener('mouseup', this.boundHandleMouseUp);
        this.canvas.removeEventListener('mouseout', this.boundHandleMouseUp);
        this.canvas.removeEventListener('touchstart', this.boundHandleTouchStart);
        this.canvas.removeEventListener('touchmove', this.boundHandleTouchMove);
        this.canvas.removeEventListener('touchend', this.boundHandleTouchEnd);
    }

    private handleMouseDown(e: MouseEvent): void {
        e.preventDefault();
        this.isDrawing = true;
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        this.lastX = (e.clientX - rect.left) * scaleX;
        this.lastY = (e.clientY - rect.top) * scaleY;



        this.lastTime = Date.now();
        this.drawStart.emit(e);
    }

    private handleMouseMove(e: MouseEvent): void {
        e.preventDefault();
        if (!this.isDrawing) return;

        const currentTime = Date.now();
        const timeElapsed = currentTime - this.lastTime;
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        const currentX = (e.clientX - rect.left) * scaleX;
        const currentY = (e.clientY - rect.top) * scaleY;

        this.velocity = Math.sqrt(Math.pow(currentX - this.lastX, 2) + Math.pow(currentY - this.lastY, 2)) / timeElapsed;
        this.velocity = this.velocity * 0.4 + this.lastVelocity * 0.6;

        this.drawLine(this.lastX, this.lastY, currentX, currentY);

        this.lastX = currentX;
        this.lastY = currentY;
        this.lastVelocity = this.velocity;
        this.lastTime = currentTime;
    }

    private handleMouseUp(e: MouseEvent): void {
        e.preventDefault();
        this.isDrawing = false;
        this.drawEnd.emit(e);
    }

    private handleTouchStart(e: TouchEvent): void {
        e.preventDefault();
        const touch = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        const x = (touch.clientX - rect.left) * scaleX;
        const y = (touch.clientY - rect.top) * scaleY;

        this.isDrawing = true;
        this.lastX = x;
        this.lastY = y;
        this.lastTime = Date.now();
        this.drawStart.emit(touch);
    }

    private handleTouchMove(e: TouchEvent): void {
        e.preventDefault();
        if (!this.isDrawing) return;

        const touch = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        const currentX = (touch.clientX - rect.left) * scaleX;
        const currentY = (touch.clientY - rect.top) * scaleY;

        const currentTime = Date.now();
        const timeElapsed = currentTime - this.lastTime;

        this.velocity = Math.sqrt(Math.pow(currentX - this.lastX, 2) + Math.pow(currentY - this.lastY, 2)) / timeElapsed;
        this.velocity = this.velocity * 0.4 + this.lastVelocity * 0.6;

        this.drawLine(this.lastX, this.lastY, currentX, currentY);

        this.lastX = currentX;
        this.lastY = currentY;
        this.lastVelocity = this.velocity;
        this.lastTime = currentTime;
    }

    private handleTouchEnd(e: TouchEvent): void {
        e.preventDefault();
        this.isDrawing = false;
        if (e.changedTouches.length > 0) {
            this.drawEnd.emit(e.changedTouches[0]);
        }
    }

    private drawLine(fromX: number, fromY: number, toX: number, toY: number): void {
        const minWidth = this.options.minWidth || 1;
        const maxWidth = this.options.maxWidth || 1;

        // Enhanced velocity calculation for stronger strokes
        const velocityFactor = Math.max(0.1, this.velocity / 1000); // Normalize velocity
        const width = Math.max(minWidth, Math.min(maxWidth, maxWidth * (1 - velocityFactor * 0.5)));

        this.ctx.lineWidth = width;
        this.ctx.beginPath();
        this.ctx.moveTo(fromX, fromY);
        this.ctx.lineTo(toX, toY);
        this.ctx.stroke();
    }

    // Public methods
    clear(): void {
        this.ctx.fillStyle = this.options.backgroundColor || 'rgb(255,255,255)';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    toDataURL(type?: string, quality?: number): string {
        return this.canvas.toDataURL(type, quality);
    }

    fromDataURL(dataURL: string, options?: { width?: number; height?: number }): void {
        const img = new Image();
        img.onload = () => {
            this.clear();
            if (options?.width && options?.height) {
                this.ctx.drawImage(img, 0, 0, options.width, options.height);
            } else {
                this.ctx.drawImage(img, 0, 0);
            }
        };
        img.src = dataURL;
    }

    isEmpty(): boolean {
        const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] > 0) { // Check alpha channel
                return false;
            }
        }
        return true;
    }

    set(option: keyof SignaturePadOptions, value: any): void {
        if (option in this.options) {
            (this.options as any)[option] = value;

            if (option === 'canvasWidth' || option === 'canvasHeight') {
                this.resizeCanvas();
            } else if (option === 'backgroundColor') {
                this.setupCanvas();
            } else if (option === 'penColor') {
                this.ctx.strokeStyle = value;
            }
        }
    }
}
